import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import {
  Zap, ShowerHead, Armchair, Wifi, UtensilsCrossed, ShieldCheck,
  Clock, WifiOff, MapPin, PlugZap, Coffee, Navigation,
  BatteryCharging, Bell, CreditCard, Gift, ArrowRight, Users,
} from "lucide-react";
import Seo from "@/components/site/Seo";
import { Reveal } from "@/components/site/Reveal";
import { Overline } from "@/components/site/primitives";
import ConceptTag from "@/components/site/ConceptTag";
import WaitlistForm from "@/components/site/WaitlistForm";
import { IMG } from "@/lib/content";

const EASE = [0.16, 1, 0.3, 1];

const scrollToWaitlist = () => {
  const el = document.getElementById("waitlist");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const STATS = [
  { value: "1,500+", label: "Early Waitlist Signups" },
  { value: "6-in-1", label: "Charge · Eat · Work · Relax · Shop · Rest" },
  { value: "In Dev", label: "Building our first destination" },
];

const PROBLEMS = [
  { icon: Clock, title: "Long, dead waits", text: "Charging can take 30–60+ minutes — time too often spent sitting in the car with nothing to do." },
  { icon: ShowerHead, title: "No clean restrooms", text: "Many charging locations lack clean, well-maintained facilities." },
  { icon: Armchair, title: "Nowhere comfortable", text: "Few offer comfortable indoor seating to actually relax while you wait." },
  { icon: UtensilsCrossed, title: "Food as an afterthought", text: "Quality food and refreshments are rarely part of the stop." },
  { icon: WifiOff, title: "No place to work", text: "Spotty connectivity and no workspace make the time hard to use." },
  { icon: MapPin, title: "Not always welcoming", text: "A safe, well-lit, welcoming place to spend charging time is hard to find." },
];

const FEATURES = [
  { icon: Zap, title: "Fast EV Charging", text: "Future DC fast-charging infrastructure designed to get you back on the road quickly." },
  { icon: ShowerHead, title: "Clean Restrooms", text: "Clean, modern, well-maintained facilities you'll actually want to use." },
  { icon: Armchair, title: "Comfortable Lounge", text: "Warm, thoughtfully designed seating built for a real break." },
  { icon: Wifi, title: "Wi-Fi + Workspace", text: "High-speed Wi-Fi and quiet spaces to answer emails or take a call." },
  { icon: Coffee, title: "Food & Refreshments", text: "Quality coffee, food, and grab-and-go options for the road ahead." },
  { icon: ShieldCheck, title: "Safe & Convenient", text: "A well-lit, welcoming environment designed around your peace of mind." },
];

const STEPS = [
  { icon: PlugZap, num: "01", title: "Pull In & Plug In", text: "Arrive at RestPower and begin charging your vehicle." },
  { icon: Coffee, num: "02", title: "Recharge Yourself", text: "Relax, use the restroom, grab something to eat, get work done, or enjoy the lounge." },
  { icon: Navigation, num: "03", title: "Get Back on the Road", text: "Get a notification when your vehicle is ready and continue your journey — refreshed." },
];

const APP_FEATURES = [
  { icon: BatteryCharging, label: "Charging status" },
  { icon: Bell, label: "Notifications" },
  { icon: CreditCard, label: "Payments" },
  { icon: Gift, label: "Rewards" },
  { icon: MapPin, label: "Location discovery" },
];

export default function BetterStop() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);

  return (
    <>
      <Seo
        title="RestPower | More Than a Charge. It's a Better Stop."
        description="RestPower is reimagining the EV charging experience with comfortable lounges, clean restrooms, Wi-Fi, food and convenient amenities while your vehicle charges."
        path="/better-stop"
      />

      {/* 1. HERO */}
      <section ref={heroRef} className="relative flex min-h-[100svh] items-center overflow-hidden pt-[72px]" data-testid="bs-hero">
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <img src={IMG.exterior} alt="RestPower charging destination concept" className="h-full w-full object-cover" fetchPriority="high" />
        </motion.div>
        <div className="absolute inset-0 bg-black/65" />
        <div className="absolute inset-0 bg-gradient-to-t from-rp-bg via-rp-bg/30 to-black/40" />
        <ConceptTag className="absolute right-6 top-24 md:right-12" />

        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 py-20 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 rounded-full border border-rp-accent/40 bg-rp-accent/10 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-rp-accent">
              <Users className="h-3.5 w-3.5" /> 1,500+ Early Waitlist Signups
            </span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.08 }}
            className="mt-7 max-w-4xl font-display text-5xl font-light uppercase leading-[0.92] tracking-tight text-white sm:text-6xl md:text-7xl lg:text-8xl"
          >
            More Than a Charge.<br /><span className="text-rp-accent">It's a Better Stop.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.18 }}
            className="mt-7 font-display text-2xl font-light text-white/90 md:text-3xl"
          >
            Your car needs a charge. You deserve a break.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.26 }}
            className="mt-5 max-w-xl text-base leading-relaxed text-white/65 md:text-lg"
          >
            RestPower is reimagining the EV charging stop as a comfortable destination — so you're not
            stuck sitting in your vehicle while it charges. Recharge yourself while your car recharges.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.34 }}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <button onClick={scrollToWaitlist} data-testid="bs-hero-primary-cta"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-rp-accent px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-black transition-colors duration-200 hover:bg-white">
              Join the RestPower Waitlist
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            <Link to="/experience" data-testid="bs-hero-secondary-cta"
              className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-white transition-colors duration-200 hover:border-rp-accent hover:text-rp-accent">
              Discover the RestPower Experience
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* 2. SOCIAL PROOF */}
      <section className="border-y border-white/10 bg-rp-surface" data-testid="bs-social-proof">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-px overflow-hidden px-6 py-12 sm:grid-cols-3 md:px-12">
          {STATS.map((s, i) => (
            <Reveal key={s.value} delay={i * 0.08} className="px-6 py-4 text-center sm:text-left">
              <div className="font-display text-4xl font-light text-rp-accent md:text-5xl">{s.value}</div>
              <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.14em] text-white/50">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 3. THE PROBLEM */}
      <section className="bg-rp-bg" data-testid="bs-problem">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <Reveal className="max-w-3xl">
            <Overline>The Problem</Overline>
            <h2 className="mt-5 font-display text-4xl font-light uppercase leading-[0.95] tracking-tight md:text-5xl">
              Charging shouldn't feel like a chore.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-white/55">
              EV charging can require drivers to wait 30–60+ minutes. Yet many charging locations still
              leave you with nowhere comfortable to spend that time.
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PROBLEMS.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 0.06}>
                <div className="h-full rounded-2xl border border-white/10 bg-rp-card p-7" data-testid={`bs-problem-${i}`}>
                  <p.icon className="h-6 w-6 text-white/40" strokeWidth={1.5} />
                  <h3 className="mt-5 font-display text-lg font-medium tracking-tight text-white">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/55">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 4. THE RESTPOWER EXPERIENCE */}
      <section className="border-y border-white/10 bg-rp-surface" data-testid="bs-experience">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <Reveal className="max-w-3xl">
            <Overline>The RestPower Experience</Overline>
            <h2 className="mt-5 font-display text-4xl font-light uppercase leading-[0.95] tracking-tight md:text-5xl">
              The hospitality layer around EV charging.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-white/55">
              RestPower is designed around both the vehicle and the person waiting for it — everything you
              need for a genuinely better stop.
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={(i % 3) * 0.06}>
                <div className="group h-full rounded-2xl border border-white/10 bg-rp-card p-8 transition-colors duration-300 hover:border-rp-accent/40" data-testid={`bs-feature-${i}`}>
                  <div className="grid h-12 w-12 place-items-center rounded-xl border border-white/10 text-rp-accent transition-colors duration-300 group-hover:border-rp-accent/50 group-hover:shadow-glow-sm">
                    <f.icon className="h-6 w-6" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-6 font-display text-xl font-medium tracking-tight text-white">{f.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/55">{f.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 5. HOW IT WORKS */}
      <section className="bg-rp-bg" data-testid="bs-how">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <Reveal className="max-w-3xl">
            <Overline>How It Works</Overline>
            <h2 className="mt-5 font-display text-4xl font-light uppercase leading-[0.95] tracking-tight md:text-5xl">
              Three steps to a better stop.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.1}>
                <div className="relative h-full overflow-hidden rounded-2xl border border-white/10 bg-rp-card p-8" data-testid={`bs-step-${i}`}>
                  <span className="font-mono text-6xl font-light text-white/10">{s.num}</span>
                  <div className="mt-4 grid h-12 w-12 place-items-center rounded-xl border border-rp-accent/30 text-rp-accent">
                    <s.icon className="h-6 w-6" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-light uppercase tracking-tight text-white">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/55">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 6. APP / TECHNOLOGY */}
      <section className="border-y border-white/10 bg-rp-surface" data-testid="bs-app">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
            <Reveal>
              <Overline>Technology · Planned</Overline>
              <h2 className="mt-5 font-display text-4xl font-light uppercase leading-[0.95] tracking-tight md:text-5xl">
                A RestPower app, by design.
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-white/55">
                We're designing a RestPower app experience to make every stop seamless. These features are
                planned for the future and are not yet available today.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {APP_FEATURES.map((a) => (
                  <span key={a.label} className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-rp-card px-4 py-2.5 text-sm text-white/70">
                    <a.icon className="h-4 w-4 text-rp-accent" strokeWidth={1.5} /> {a.label}
                  </span>
                ))}
              </div>
              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.14em] text-white/35">
                Planned functionality — not currently available.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="relative overflow-hidden rounded-3xl border border-white/10">
                <img src={IMG.lounge} alt="RestPower lounge concept" loading="lazy" className="h-[380px] w-full object-cover md:h-[480px]" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <ConceptTag className="absolute left-5 top-5" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 7. FOUNDER STORY */}
      <section className="bg-rp-bg" data-testid="bs-founder">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <div className="grid gap-14 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-5">
              <div className="relative overflow-hidden rounded-3xl border border-white/10">
                <img src={IMG.road} alt="On the road" loading="lazy" className="h-[360px] w-full object-cover md:h-[460px]" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </div>
            </Reveal>
            <Reveal delay={0.08} className="lg:col-span-7">
              <Overline>Our Story</Overline>
              <h2 className="mt-5 font-display text-4xl font-light uppercase leading-[0.95] tracking-tight md:text-5xl">
                Built from the driver's seat.
              </h2>
              <div className="mt-6 space-y-5 text-base leading-relaxed text-white/60 md:text-lg">
                <p>
                  RestPower was inspired by firsthand experience driving Uber and Lyft full-time in rented
                  electric vehicles. Long charging stops created a recurring problem: choosing between
                  charging the car and taking care of basic necessities — a restroom, a meal, a comfortable
                  place to wait.
                </p>
                <p>
                  That gap led to a simple conviction: charging destinations should be built around both the
                  vehicle <span className="text-white">and</span> the person driving it. RestPower is our
                  answer — a better stop for the people who spend the most time on the road.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 8. FINAL CTA + WAITLIST FORM */}
      <section id="waitlist" className="scroll-mt-20 border-t border-white/10 bg-rp-surface" data-testid="bs-waitlist-section">
        <div className="mx-auto max-w-4xl px-6 py-24 text-center md:px-12 md:py-32">
          <Reveal>
            <Overline className="justify-center">Join the Waitlist</Overline>
            <h2 className="mt-5 font-display text-4xl font-light uppercase leading-[0.95] tracking-tight md:text-6xl">
              Ready for a Better Stop?
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-white/55 md:text-lg">
              Join the growing RestPower community and be among the first to know when RestPower is coming
              to your area.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="mt-12">
            <div className="rounded-3xl border border-white/10 bg-rp-card p-7 text-left md:p-10">
              <WaitlistForm />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
